import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import './incoming-call-modal.scss';
import { useEffect, useRef, useState } from 'react';
import incomingRingtoneSrc from '@/assets/softphone/freesound_community-original-phone-ringtone-36558.mp3';
import { CallControlIcon } from '@/components/call-control/CallControlIcons';
const INCOMING_RING_VOLUME = 0.42;
function callerInitials(label) {
    const t = label.trim();
    if (!t)
        return '?';
    const parts = t.split(/\s+/).filter(Boolean);
    if (parts.length >= 2) {
        const a = parts[0][0];
        const b = parts[parts.length - 1][0];
        return `${a ?? ''}${b ?? ''}`.toUpperCase();
    }
    return t.slice(0, 2).toUpperCase();
}
/** Top-centered toast while ringing only; draggable. Mute/Hold remain on the main call toolbar. */
export default function IncomingCallModal({ visible, callerLabel, calledNumber, queueName, onAnswer, onDecline, }) {
    const [position, setPosition] = useState(null);
    const dragRef = useRef({ active: false, offsetX: 0, offsetY: 0 });
    const ringAudioRef = useRef(null);
    /** Ring tone only while toast is visible (ringing). */
    useEffect(() => {
        if (!visible) {
            const el = ringAudioRef.current;
            if (el) {
                el.pause();
                el.currentTime = 0;
            }
            return;
        }
        let audio = ringAudioRef.current;
        if (!audio) {
            audio = new Audio(incomingRingtoneSrc);
            audio.loop = true;
            ringAudioRef.current = audio;
        }
        audio.volume = INCOMING_RING_VOLUME;
        void audio.play().catch(() => undefined);
        return () => {
            const el = ringAudioRef.current;
            if (el) {
                el.pause();
                el.currentTime = 0;
            }
        };
    }, [visible]);
    useEffect(() => {
        if (!visible)
            setPosition(null);
    }, [visible]);
    useEffect(() => {
        const move = (e) => {
            if (!dragRef.current.active)
                return;
            setPosition({
                x: e.clientX - dragRef.current.offsetX,
                y: e.clientY - dragRef.current.offsetY,
            });
        };
        const stop = () => {
            dragRef.current.active = false;
            document.body.style.userSelect = '';
        };
        window.addEventListener('mousemove', move);
        window.addEventListener('mouseup', stop);
        return () => {
            window.removeEventListener('mousemove', move);
            window.removeEventListener('mouseup', stop);
        };
    }, []);
    const startDrag = (e) => {
        if (e.button !== 0)
            return;
        const root = e.currentTarget.closest('.incoming-call-modal-root');
        if (!root)
            return;
        const rect = root.getBoundingClientRect();
        dragRef.current = {
            active: true,
            offsetX: e.clientX - rect.left,
            offsetY: e.clientY - rect.top,
        };
        document.body.style.userSelect = 'none';
        if (position === null) {
            setPosition({ x: rect.left, y: rect.top });
        }
    };
    if (!visible)
        return null;
    const label = callerLabel || 'Unknown caller';
    const called = calledNumber?.trim() || null;
    const queue = queueName?.trim() || null;
    return (_jsx("aside", { className: `incoming-call-modal-root incoming-call-modal-root--toast${position ? ' incoming-call-modal-root--dragging' : ''}`, role: "alertdialog", "aria-modal": "true", "aria-live": "assertive", "aria-labelledby": "incoming-call-modal-title", "aria-describedby": "incoming-call-modal-subtitle", style: position
            ? {
                position: 'fixed',
                left: position.x,
                top: position.y,
                right: 'auto',
                bottom: 'auto',
            }
            : undefined, children: _jsxs("div", { className: "incoming-call-modal__panel incoming-call-modal__panel--incoming", children: [_jsx("div", { className: "incoming-call-modal__pulse", "aria-hidden": true }), _jsx("div", { className: "incoming-call-modal__sheen", "aria-hidden": true }), _jsx("button", { type: "button", className: "incoming-call-modal__drag-handle", "aria-label": "Move incoming call toast", title: "Drag to move", onMouseDown: startDrag }), _jsxs("div", { className: "incoming-call-modal__content", children: [_jsx("div", { className: "incoming-call-modal__avatar", "aria-hidden": "true", children: callerInitials(label) }), _jsxs("div", { className: "incoming-call-modal__text", children: [_jsx("h2", { id: "incoming-call-modal-title", className: "incoming-call-modal__title", children: "Incoming call" }), _jsx("p", { id: "incoming-call-modal-subtitle", className: "incoming-call-modal__caller", children: label }), called ? (_jsxs("p", { className: "incoming-call-modal__meta", children: ["Called number: ", called] })) : null, queue ? _jsxs("p", { className: "incoming-call-modal__meta", children: ["Queue: ", queue] }) : null, _jsxs("p", { className: "incoming-call-modal__subtext incoming-call-modal__subtext--ring", children: ["Ringing", _jsx("span", { className: "incoming-call-modal__ellipsis", "aria-hidden": true, children: "\u2026" })] })] }), _jsxs("div", { className: "incoming-call-modal__toolbar", role: "toolbar", "aria-label": "Incoming call actions", children: [_jsxs("button", { type: "button", className: "incoming-call-modal__icon-btn incoming-call-modal__icon-btn--answer", title: "Answer call", "aria-label": "Answer call", onClick: () => void onAnswer(), children: [_jsx("span", { className: "incoming-call-modal__icon", children: _jsx(CallControlIcon, { glyph: "answer" }) }), _jsx("span", { className: "incoming-call-modal__btn-label", children: "Answer" })] }), _jsxs("button", { type: "button", className: "incoming-call-modal__icon-btn incoming-call-modal__icon-btn--danger", title: "Decline call", "aria-label": "Decline call", onClick: () => void onDecline(), children: [_jsx("span", { className: "incoming-call-modal__icon", children: _jsx(CallControlIcon, { glyph: "hangup" }) }), _jsx("span", { className: "incoming-call-modal__btn-label", children: "Decline" })] })] })] })] }) }));
}
